import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CampaignStatsChart from './CampaignStatsChart';
import { PeopleFill, HeartPulseFill, CheckLg, XLg, ArrowCounterclockwise, PencilSquare } from 'react-bootstrap-icons';
import { updateBasketKillDate, getServerNow } from '../services/api';

// 서버 값('YYYY-MM-DD HH:mm:ss' 등)을 Date 객체로 변환
const parseServerDate = (value) => {
  if (!value) return null;
  const d = new Date(String(value).replace(' ', 'T'));
  return isNaN(d.getTime()) ? null : d;
};

const pad = (n) => String(n).padStart(2, '0');

// Date 객체를 서버가 기대하는 'YYYY-MM-DD HH:mm:ss'로 변환
const toBackendDatetime = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

// 실사용상 날짜를 바꾸는 경우는 거의 없고 분 단위로 시간만 밀고 당기는 경우가 대부분이라,
// 평소엔 이 버튼들만 노출하고 달력 편집기는 연필 아이콘 뒤로 숨긴다.
const MINUTE_NUDGES = [-10, -5, -1, 1, 5, 10];

// 숫자만 남기고 자릿수/최댓값으로 잘라낸 문자열로 정규화 (빈 문자열은 그대로 허용 - 입력 중 지우는 중간 상태)
const sanitizeDigits = (raw, max) => {
  const digits = raw.replace(/\D/g, '').slice(0, 2);
  if (digits === '') return '';
  return String(Math.min(Number(digits), max));
};

// 네이티브 <input type="time">은 OS/브라우저 로캘에 따라 오전/오후 12시간제로 표시되는 경우가 많아
// (macOS Chrome은 시스템 지역 설정을 따름) 신뢰할 수 없다. 시/분을 각각 숫자 입력으로 받아 항상 24시간제로 고정한다.
// initialDate 기준으로 최초 1회만 시/분을 초기화하고, 그룹 밖으로 포커스가 나가거나 Enter를 누르면 onCommit(hours, minutes) 호출.
const TimeInput24 = ({ initialDate, onCommit, disabled }) => {
  const [hour, setHour] = useState(pad(initialDate.getHours()));
  const [minute, setMinute] = useState(pad(initialDate.getMinutes()));

  const commit = () => {
    if (hour === '' || minute === '') return;
    const h = Number(hour);
    const m = Number(minute);
    if (!Number.isInteger(h) || !Number.isInteger(m)) return;
    onCommit(h, m);
  };

  const handleGroupBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) commit();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
      e.currentTarget.blur();
    }
  };

  return (
    <div className="d-flex align-items-center gap-1" onBlur={handleGroupBlur}>
      <input
        type="text"
        inputMode="numeric"
        className="form-control form-control-sm text-center"
        style={{ width: '46px' }}
        value={hour}
        onChange={(e) => setHour(sanitizeDigits(e.target.value, 23))}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label="시(0-23)"
      />
      <span className="text-muted">:</span>
      <input
        type="text"
        inputMode="numeric"
        className="form-control form-control-sm text-center"
        style={{ width: '46px' }}
        value={minute}
        onChange={(e) => setMinute(sanitizeDigits(e.target.value, 59))}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label="분(0-59)"
      />
    </div>
  );
};

// 게임타입 2 전용: killdt(자동 파괴 시각) 표시 및 인라인 편집
const KillDateField = ({ basketKey, killdt }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  // 저장 직후 값. 다음 대시보드 갱신(웹소켓/재조회) 전까지 화면에 즉시 반영하기 위함
  const [savedValue, setSavedValue] = useState(null);

  const displayValue = savedValue !== null ? savedValue : killdt;
  const displayDate = parseServerDate(displayValue);

  const persist = async (date) => {
    setIsSaving(true);
    setError(null);
    try {
      const backendValue = date ? toBackendDatetime(date) : null;
      await updateBasketKillDate(basketKey, backendValue);
      setSavedValue(backendValue);
      return true;
    } catch (err) {
      setError(err.message || 'killdt 수정에 실패했습니다.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // 분 단위로 바로 밀고 당기기 (가장 흔한 조작) - 편집기를 열지 않고 즉시 저장
  const nudgeMinutes = (minutes) => {
    if (!displayDate || isSaving) return;
    persist(new Date(displayDate.getTime() + minutes * 60000));
  };

  // 시간만 숫자로 직접 타이핑해서 즉시 반영 (그룹 밖으로 blur 또는 Enter 시 저장) - 날짜는 유지
  const commitQuickTime = (hours, minutes) => {
    if (!displayDate) return;
    const next = new Date(displayDate);
    next.setHours(hours, minutes, 0, 0);
    if (next.getTime() === displayDate.getTime()) return;
    persist(next);
  };

  const startEdit = () => {
    setSelectedDate(displayDate || new Date());
    setError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const saveEdit = async () => {
    if (await persist(selectedDate)) setIsEditing(false);
  };

  const clearKillDate = async () => {
    if (await persist(null)) setIsEditing(false);
  };

  // 날짜(달력)만 변경 - 시간은 유지
  const handleDateChange = (date) => {
    if (!date) return;
    setSelectedDate((prev) => {
      const next = prev ? new Date(prev) : new Date();
      next.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      return next;
    });
  };

  // 시간(시:분)만 숫자로 직접 입력 - 날짜는 유지
  const handleTimeCommit = (hours, minutes) => {
    setSelectedDate((prev) => {
      const next = prev ? new Date(prev) : new Date();
      next.setHours(hours, minutes, 0, 0);
      return next;
    });
  };

  if (!isEditing) {
    return (
      <div className="mb-2">
        <div className="d-flex align-items-center gap-1">
          <span className="text-info small">
            자동 파괴: {displayDate ? displayDate.toLocaleString() : '미설정'}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary py-0 px-1 border-0"
            onClick={startEdit}
            title="날짜 변경 등 상세 편집"
          >
            <PencilSquare size={12} />
          </button>
          {isSaving && <span className="spinner-border spinner-border-sm text-secondary" />}
        </div>
        {displayDate && (
          <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
            <TimeInput24
              key={displayValue || 'unset'}
              initialDate={displayDate}
              onCommit={commitQuickTime}
              disabled={isSaving}
            />
            <div className="btn-group btn-group-sm" role="group" aria-label="분 단위 시간 조정">
              {MINUTE_NUDGES.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`btn ${m < 0 ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                  onClick={() => nudgeMinutes(m)}
                  disabled={isSaving}
                  title={`${m > 0 ? '+' : ''}${m}분`}
                >
                  {m > 0 ? `+${m}` : m}
                </button>
              ))}
            </div>
          </div>
        )}
        {error && <div className="text-danger small mt-1">{error}</div>}
      </div>
    );
  }

  return (
    <div className="card card-body p-2 mb-2 bg-light-subtle">
      <div className="d-flex flex-column gap-1">
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          dateFormat="yyyy-MM-dd"
          className="form-control form-control-sm w-100"
          wrapperClassName="w-100"
          placeholderText="날짜 선택"
          disabled={isSaving}
          withPortal
          portalId="killdt-datepicker-portal"
        />
        <TimeInput24
          initialDate={selectedDate || new Date()}
          onCommit={handleTimeCommit}
          disabled={isSaving}
        />
      </div>
      <div className="d-flex justify-content-between gap-1 mt-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={clearKillDate}
          disabled={isSaving}
          title="자동 파괴 시각 해제"
        >
          <ArrowCounterclockwise size={12} className="me-1" />
          해제
        </button>
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn btn-sm btn-success"
            onClick={saveEdit}
            disabled={isSaving || !selectedDate}
          >
            {isSaving ? <span className="spinner-border spinner-border-sm" /> : <CheckLg size={14} />}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={cancelEdit}
            disabled={isSaving}
          >
            <XLg size={14} />
          </button>
        </div>
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

const GourdBreakingReport = ({ randomBoxEvents, totalSignups }) => {
  const [now, setNow] = useState(() => getServerNow());

  useEffect(() => {
    const timer = setInterval(() => setNow(getServerNow()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!randomBoxEvents || randomBoxEvents.length === 0) {
    return <p>표시할 박 터트리기 이벤트가 없습니다.</p>;
  }

  return (
    <div>
      {randomBoxEvents.map(event => {
        const itemsToShow = event.items ? event.items.filter(item => item.itemname !== '박이 터졌어요') : [];
        const firstBasket = event.baskets[0];
        const hpPercent = firstBasket && firstBasket.maxHP > 0 
          ? (firstBasket.currentHP / firstBasket.maxHP) * 100 
          : 0;
        
        const getProgressBarVariant = (percentage) => {
          if (percentage > 50) return 'bg-success';
          if (percentage > 20) return 'bg-warning';
          return 'bg-danger';
        };

        return (
          <div key={event.eventKey} className="card mb-4 shadow-sm">
            <div className="card-header bg-primary text-white">
              <h3 className="mb-0">{event.eventName} ({event.statusName})</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-7">
                  <h4 className="mb-3"><PeopleFill className="me-2" />참여 현황</h4>
                  <CampaignStatsChart 
                    signupCount={totalSignups}
                    participantCount={event.participantCount}
                    todaySignups={event.todaySignups || 0}
                  />
                </div>
                <div className="col-md-5">
                  <h4 className="mb-3"><HeartPulseFill className="me-2" />박 HP 정보</h4>
                  {event.baskets && event.baskets.map(basket => (
                     <div key={basket.basketKey} className="card mb-3">
                       <div className="card-body">
                         <h5 className="card-title">{basket.basketName}</h5>
                         <div className="progress" style={{ height: '25px', fontSize: '1rem' }}>
                           <div 
                             className={`progress-bar ${getProgressBarVariant(hpPercent)}`}
                             role="progressbar" 
                             style={{ width: `${hpPercent}%` }} 
                             aria-valuenow={hpPercent} 
                             aria-valuemin="0" 
                             aria-valuemax="100"
                           >
                             {hpPercent.toFixed(2)}%
                           </div>
                         </div>
                         <p className="text-muted mt-2 mb-1">
                           {basket.currentHP.toLocaleString()} / {basket.maxHP.toLocaleString()}
                         </p>
                         <p className="text-muted small mb-2">현재 시각: {now.toLocaleString()}</p>
                         <KillDateField basketKey={basket.basketKey} killdt={basket.killdt} />
                         {basket.bonusrate !== undefined && (
                           <p className="card-text mb-0">
                             <strong>보물상자 나올 확률:</strong> {(basket.bonusrate * 100).toFixed(0)}%
                           </p>
                         )}
                         {basket.loserate !== undefined && (
                           <p className="card-text mb-0">
                             <strong>꽝일 확률:</strong> {(basket.loserate * 100).toFixed(0)}%
                           </p>
                         )}
                       </div>
                     </div>
                  ))}
                  {/* Stats Card */}
                  <div className="card mb-3">
                    <div className="card-header">
                      <h5 className="mb-0">이벤트 요약</h5>
                    </div>
                    <div className="card-body">
                      <p className="card-text">
                        <PeopleFill className="me-2" />
                        <strong>참가자:</strong> {event.participantCount} 명
                      </p>
                      <p className="card-text">
                        <PeopleFill className="me-2" />
                        <strong>오늘 가입자:</strong> {event.todaySignups || 0} 명
                      </p>
                      <p className="card-text">
                        <PeopleFill className="me-2" />
                        <strong>브랜드 노출 횟수:</strong> {event.brandExposure} 회
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <h4 className="mt-4">상품 목록</h4>
              {itemsToShow.length > 0 ? (
                <table className="table table-striped table-bordered table-hover mt-2">
                  <thead className="table-dark">
                    <tr>
                      <th>상품명</th>
                      <th>수량</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsToShow.map(item => (
                      <tr key={item.itemkey}>
                        <td>{item.itemname}</td>
                        <td>{item.itemcount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="alert alert-info mt-2">표시할 상품이 없습니다. ('박이 터졌어요' 상품은 제외됩니다.)</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GourdBreakingReport;